/**
 * Utility functions for image manipulation and sharing
 * @author Random Pokemon Generator
 * @copyright 2023-2024
 */

import { toast } from 'sonner';
import html2canvas from 'html2canvas';

/**
 * 下载元素为图片
 * @param elementId 需要截图的元素ID
 * @param fileName 保存的文件名
 * @returns 是否成功下载
 */
export const downloadElementAsImage = async (elementId: string, fileName: string): Promise<boolean> => {
  try {
    // 查找目标元素
    const element = document.getElementById(elementId);
    if (!element) {
      toast.error('未找到要截图的元素');
      return false;
    }

    // 在截图前添加一个临时类，使元素保持原样，不包含按钮部分
    element.classList.add('screenshot-mode');
    
    // 使用html2canvas捕获元素
    // 使用类型断言处理类型问题
    const options = {
      allowTaint: true,
      useCORS: true, // 允许跨域图片
      logging: false,
      background: '#ffffff',
      onclone: (clonedDoc: Document) => {
        // 在克隆的文档中查找按钮容器并隐藏它
        const clonedElement = clonedDoc.getElementById(elementId);
        if (clonedElement) {
          const footer = clonedElement.querySelector('.card-footer');
          if (footer && footer instanceof HTMLElement) {
            footer.style.display = 'none';
          }
        }
      }
    } as any; // 使用any类型断言绕过类型检查
    
    const canvas = await html2canvas(element, options);
    
    // 截图后移除临时类
    element.classList.remove('screenshot-mode');
    
    // 将canvas转换为数据URL
    const dataUrl = canvas.toDataURL('image/png');
    
    // 创建下载链接
    const link = document.createElement('a');
    link.download = fileName;
    link.href = dataUrl;
    link.click();
    
    toast.success(`图片 ${fileName} 已下载!`);
    return true;
  } catch (error) {
    console.error('生成图片时出错:', error);
    toast.error('生成图片失败，请重试。');
    return false;
  }
};

// Function to share to social media (basic implementation that opens share dialogs)
export const shareToSocialMedia = (
  platform: 'facebook' | 'twitter' | 'whatsapp',
  text: string,
  url: string = window.location.href
) => {
  let shareUrl = '';
  
  switch (platform) {
    case 'facebook':
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
      break;
    case 'twitter':
      shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
      break;
    case 'whatsapp':
      shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
      break;
  }
  
  if (shareUrl) {
    // Open in new window
    window.open(shareUrl, '_blank', 'width=600,height=400');
    return true;
  }
  
  return false;
};
